import { FormInstance, Image, Rate, Select } from 'antd';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { RouteComponentProps } from 'react-router-dom';
import positionApi from '../../apis/PositionApi';
import prospectsApi from '../../apis/ProspectsApi';
import seasonApi from '../../apis/SeasonApi';
import sportApi from '../../apis/SportApi';
import userRatingApi from '../../apis/UserRatingApi';
import userSportApi from '../../apis/UserSportApi';
import LayoutComponent from '../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../context/CustomContext';
import { Position, Season, Sport, User, UserOrganizationMember, UserRating, UserSport } from '../../model/Entities';
import avatar from '../../resources/images/profile-placeholder.png';
import logo from '../../resources/images/top100-logo.png';
import ga from '../../services/GoogleAnalytics';
import HeadMetadata from '../../services/HeadMetadata';
import metricService from '../../services/MetricService';
import notificationService from '../../services/NotificationService';
import playerService from '../../services/PlayerService';
import styles from './PlayerProfilePage.module.scss';

class PlayerProfilePage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();
    readonly pageSize = 10;

    constructor(props: Props) {
        super(props);
        this.state = { positions: [], userSports: [], seasons: [], userOrganizationsBySport: [] };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: 'initializing' });

            // get player
            const responses = await Promise.all([playerService.getPlayer(this.props), sportApi.listAll()]);
            const player = responses[0];
            const sports = responses[1];

            const moreResponses = await Promise.all([
                positionApi.list(player.sport.id!),
                seasonApi.listAll(player.sport.id!),
            ]);
            const positions = moreResponses[0];
            const seasons = moreResponses[1];

            const seasonId = seasons.find((s) => s.isActive === true)?.id!;
            const userRatings = await userRatingApi.list(player.user.id!, player.sport.id!, seasonId);
            const userRating = userRatings.find((u) => u.sportId === player.sport.id && u.seasonId === seasonId);

            const playerData = playerService.getPlayerData(player.user!, player.sport!);

            this.setState({
                ...player,
                sports,

                positions,
                userRating,
                seasons,
                seasonId,
                ...playerData,
            });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    changeUserSport = async (userSportId: number) => {
        const { sports, user, userSports } = this.state;
        try {
            this.setState({ loading: 'loading' });
            const userSport = userSports.find((u) => u.id === userSportId);
            const sport = sports?.find((s) => s.id === userSport?.sportId);
            const responses = await Promise.all([seasonApi.listAll(sport?.id!), positionApi.list(sport?.id!)]);
            const seasons = responses[0];
            const seasonId = seasons && seasons.length > 0 ? seasons[0].id : undefined;

            const positions = responses[1];

            const userRatings = await userRatingApi.list(user?.id!, sport?.id!, seasonId!);
            const userRating = userRatings.find((u) => u.sportId === sport?.id && u.seasonId === seasonId);

            const playerData = playerService.getPlayerData(user!, sport!);

            this.props.history.push(`/players/${user!.id}/profile?sportId=${userSport?.sportId}`);

            this.setState({
                userSport,
                sport,
                positions,
                seasons,
                seasonId,
                userRating,
                ...playerData,
            });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    changeSeason = async (seasonId: number) => {
        const { user, sport } = this.state;
        try {
            this.setState({ loading: 'loading' });

            const userRatings = await userRatingApi.list(user?.id!, sport?.id!, seasonId);
            const userRating = userRatings.find((u) => u.sportId === sport?.id && u.seasonId === seasonId);

            this.setState({ userRating, seasonId });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    changeAffiliate = (organization: string) => {
        const { userOrganizationsBySport } = this.state;

        const selectedOrganization = userOrganizationsBySport.find((o) => o.organizationId === organization);
        const team =
            selectedOrganization && selectedOrganization.teams && selectedOrganization.teams.length > 0
                ? selectedOrganization.teams[0].name
                : undefined;

        this.setState({ organization, team });
    };

    rate = async (rate: number) => {
        const { user, userSport } = this.state;
        try {
            if (rate > 0) {
                if (userSport?.rate === undefined && userSport?.note === undefined) {
                    await prospectsApi.create(user?.id!, userSport?.sportId!, rate);
                } else {
                    await prospectsApi.update(user?.id!, userSport?.sportId!, rate, userSport.note);
                }

                const userSports = await userSportApi.list(user?.id!);
                const userSportAux = userSports.find((us) => us.sportId === userSport?.sportId);

                this.setState({ userSports, userSport: userSportAux });
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    callAnalyticsTrainingAppEvent = () => {
        ga.createDefaultEvent('training app', 'training app - redirect to training app page from player profile');
    };

    /*** COMPONENTS ***/

    renderHeader = (desktop: boolean): React.ReactElement | undefined => {
        const { sport, sports, user, userSport, userSports, seasons, seasonId } = this.state;

        const seasonOptions = seasons.map((season) => (
            <Select.Option key={season.id!} value={season.id!}>
                {season.name}
            </Select.Option>
        ));

        const userSportsOptions = userSports.map((us) => (
            <Select.Option key={us.id!} value={us.id!}>
                {sports?.find((s) => s.id === us.sportId)?.name}
            </Select.Option>
        ));

        if (desktop) {
            return (
                <div className={styles.header}>
                    <div className={styles.headerBackground}>
                        <div className={styles.headerBackgroundGradient}></div>
                        <div
                            className={styles.headerBackgroundImage}
                            style={{ backgroundImage: `url(${sport?.backgroundUrl})` }}
                        ></div>
                    </div>

                    <div className={styles.buttons}>
                        {this.context.auth?.authorities.includes('ROLE_COLLEGE_COACH') &&
                            sport?.id === this.context.auth.colleges?.find(Boolean)?.sportId && (
                                <Rate value={userSport?.rate} onChange={this.rate} />
                            )}
                        {user && (
                            <Select
                                className={styles.seasons}
                                size="large"
                                value={userSport?.id}
                                onChange={this.changeUserSport}
                            >
                                {userSportsOptions}
                            </Select>
                        )}
                        <Select className={styles.seasons} size="large" value={seasonId} onChange={this.changeSeason}>
                            {seasonOptions}
                        </Select>
                    </div>
                </div>
            );
        } else {
            return (
                <>
                    {this.context.auth?.authorities.includes('ROLE_COLLEGE_COACH') &&
                        sport?.id === this.context.auth.colleges?.find(Boolean)?.sportId && (
                            <Rate value={userSport?.rate} onChange={this.rate} />
                        )}
                </>
            );
        }
    };

    renderNationalRank = () => {
        const { userRating } = this.state;
        const top100Rating =
            userRating && userRating.ratings
                ? userRating.ratings.find((rating) => rating.type === 'Global')?.ratingValue
                : undefined;
        return top100Rating === 0 || !top100Rating ? <FormattedMessage id="--" /> : top100Rating;
    };

    renderAffiliateRank = () => {
        const { userRating, organization } = this.state;
        const affiliateRank =
            userRating && organization && userRating.organizationRatings && userRating.organizationRatings.length > 0
                ? userRating.organizationRatings.find((o) => o.organizationId === organization)?.rating
                : undefined;
        return affiliateRank === 0 || !affiliateRank ? <FormattedMessage id="--" /> : affiliateRank;
    };

    renderContent = (desktop: boolean): React.ReactElement | undefined => {
        const { user, userSport, team, positions, userOrganizationsBySport, organization } = this.state;
        const { auth } = this.context;
        const isParentInfoVisible =
            auth &&
            (auth.authorities.some((a) => a === 'ROLE_ADMIN') ||
                (auth.authorities.some((a) => a === 'ROLE_PLAYER') && auth.id === user?.id));

        const affiliatesOptions = userOrganizationsBySport
            .filter(
                (userOrganization, index, self) =>
                    index === self.findIndex((uo) => uo.organizationId === userOrganization.organizationId),
            )
            .map((o) => (
                <Select.Option key={o.organizationId!} value={o.organizationId!}>
                    {o.organizationName}
                </Select.Option>
            ));

        return (
            <>
                <div className={styles.mobileHeaderBackground}>
                    <div className={styles.headerBackgroundGradient}></div>
                    <div className={styles.headerBackgroundImage}></div>
                </div>
                <div className={styles.player}>
                    <header>
                        <div className={styles.picture}>
                            <Image
                                src={userSport?.photoUrl || avatar}
                                fallback="data:image/png;base64,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"
                            />
                        </div>

                        <div className={styles.playerInfo}>
                            <h1>
                                {user?.profile?.givenName} {user?.profile?.familyName}
                            </h1>

                            <ul className={styles.stats}>
                                <li>
                                    <label>
                                        <FormattedMessage id="player.profile.grade" />
                                    </label>
                                    {user?.profile?.grade}
                                </li>
                                <li>
                                    <label>
                                        <FormattedMessage id="player.profile.height" />
                                    </label>
                                    {metricService.toFormattedFeets(user?.profile?.height)}
                                </li>
                                <li>
                                    <label>
                                        <FormattedMessage id="player.profile.weight" />
                                    </label>
                                    {user?.profile?.weight}{' '}
                                    <span hidden={!user?.profile?.weight}>
                                        <FormattedMessage id="player.profile.lbs" />
                                    </span>
                                </li>
                                <li>
                                    <label>
                                        <FormattedMessage id="player.profile.positions" />
                                    </label>
                                    {positions
                                        .filter((p) => userSport?.positions?.includes(p.id))
                                        .map((pa) => pa.code)
                                        .join(',')}
                                </li>
                            </ul>

                            <div className={styles.contact}>
                                <a href={'tel:' + user?.profile.phone}>{user?.profile.phone}</a>
                                <a href={'mailto:' + user?.profile.email}>{user?.profile.email}</a>
                            </div>
                        </div>

                        <div className={styles.scores}>
                            <div className={styles.playerScore}>
                                <div>
                                    <div className={styles.score}>{this.renderNationalRank()}</div>
                                    <p>
                                        <FormattedMessage id="player.nationalRank" />
                                    </p>
                                    <img width="70" src={process.env.PUBLIC_URL + '/img/top100-logo.png'} alt={logo} />
                                </div>
                            </div>

                            <div className={styles.playerScore}>
                                <div>
                                    <div className={styles.score}>{this.renderAffiliateRank()}</div>
                                    <p>
                                        <FormattedMessage id="player.affiliateRank" />
                                    </p>
                                    <img width="70" src={process.env.PUBLIC_URL + '/img/top100-logo.png'} alt={logo} />
                                </div>
                            </div>
                        </div>

                        <div className={styles.playerStats}>
                            <ul>
                                <li hidden={!user?.profile?.coreGpa}>
                                    <label>
                                        <FormattedMessage id="player.profile.gpa" />
                                    </label>
                                    {user?.profile?.coreGpa}
                                </li>
                                <li hidden={!user?.profile?.sat}>
                                    <label>
                                        <FormattedMessage id="player.profile.sat" />
                                    </label>
                                    {user?.profile?.sat}
                                </li>
                                <li hidden={!user?.profile?.act}>
                                    <label>
                                        <FormattedMessage id="player.profile.act" />
                                    </label>
                                    {user?.profile?.act}
                                </li>
                                <li hidden={!user?.profile?.school}>
                                    <label>
                                        <FormattedMessage id="player.profile.highSchool" />
                                    </label>
                                    {user?.profile?.school}
                                </li>
                                <li hidden={!team}>
                                    <label>
                                        <FormattedMessage id="player.profile.clubTeam" />
                                    </label>
                                    {team}
                                </li>
                            </ul>
                        </div>

                        <div className={styles.sportSelect}>
                            <Select
                                className={styles.affiliates}
                                size="large"
                                value={organization}
                                onChange={this.changeAffiliate}
                            >
                                {affiliatesOptions}
                            </Select>
                        </div>
                    </header>

                    <div className={styles.trainingapp}>
                        <div className={styles.img}>
                            <img
                                src={process.env.PUBLIC_URL + '/img/training-app.png'}
                                alt="Top 100 Performance Training App."
                            />
                        </div>
                        <div className={styles.txt}>
                            <h2>
                                <FormattedMessage id="player.profile.setGoals" />
                            </h2>
                            <p>
                                <FormattedMessage id="player.profile.train" />
                            </p>
                        </div>
                        <div className={styles.cta}>
                            <p>
                                <FormattedMessage id="player.profile.introducingTrainingApp" />
                            </p>
                            <a
                                href="https://www.top100sports.com/training-app/"
                                target="_blank"
                                rel="noreferrer"
                                onClick={this.callAnalyticsTrainingAppEvent}
                            >
                                <FormattedMessage id="button.learnMore" />
                            </a>
                        </div>
                    </div>

                    {userSport?.bio && (
                        <div className={styles.about}>
                            <h2>
                                <FormattedMessage id="player.profile.about" />
                            </h2>
                            <p>{userSport?.bio}</p>
                        </div>
                    )}

                    <div className={styles.details}>
                        <div className={styles.school} hidden={!user?.profile?.coachHighSchool}>
                            <h2>
                                <FormattedMessage id="player.profile.coachInfo" />
                            </h2>
                            <h3>
                                <FormattedMessage id="player.profile.coachSchool" />
                            </h3>
                            <p>{user?.profile?.coachHighSchool}</p>
                        </div>

                        {isParentInfoVisible && (
                            <div className={styles.parent}>
                                <h2>
                                    <FormattedMessage id="player.profile.parentInfo" />
                                </h2>
                                <h3 hidden={!user?.profile?.parentName}>
                                    <FormattedMessage id="player.profile.parentName" />
                                </h3>
                                <p hidden={!user?.profile?.parentName}>{user?.profile?.parentName}</p>

                                <h3 hidden={!user?.profile?.parentPhone}>
                                    <FormattedMessage id="player.profile.parentPhone" />
                                </h3>
                                <p hidden={!user?.profile?.parentPhone}>
                                    <a href={'tel:' + user?.profile?.parentPhone}>{user?.profile?.parentPhone}</a>
                                </p>

                                <h3 hidden={!user?.profile?.parentEmail}>
                                    <FormattedMessage id="player.profile.parentEmail" />
                                </h3>
                                <p hidden={!user?.profile?.parentEmail}>
                                    <a href={'mailto:' + user?.profile?.parentEmail}>{user?.profile?.parentEmail}</a>
                                </p>
                            </div>
                        )}

                        <div
                            className={styles.social}
                            hidden={
                                !user?.profile?.facebook &&
                                !user?.profile?.instagram &&
                                !user?.profile?.twitter &&
                                !user?.profile?.snapchat
                            }
                        >
                            <h2>
                                <FormattedMessage id="player.profile.socialMedia" />
                            </h2>

                            <h3 className={styles.facebook} hidden={!user?.profile?.facebook}>
                                <FormattedMessage id="player.profile.facebook" />
                            </h3>
                            <p hidden={!user?.profile?.facebook}>
                                <a
                                    href={'https://www.facebook.com/' + user?.profile?.facebook}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {user?.profile?.facebook}
                                </a>
                            </p>

                            <h3 className={styles.instagram} hidden={!user?.profile?.instagram}>
                                <FormattedMessage id="player.profile.instagram" />
                            </h3>
                            <p hidden={!user?.profile?.instagram}>
                                <a
                                    href={'https://www.instagram.com/' + user?.profile?.instagram}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {user?.profile?.instagram}
                                </a>
                            </p>

                            <h3 className={styles.twitter} hidden={!user?.profile?.twitter}>
                                <FormattedMessage id="player.profile.twitter" />
                            </h3>
                            <p hidden={!user?.profile?.twitter}>
                                <a
                                    href={'https://twitter.com/' + user?.profile?.twitter}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {user?.profile?.twitter}
                                </a>
                            </p>

                            <h3 className={styles.snapchat} hidden={!user?.profile?.snapchat}>
                                <FormattedMessage id="player.profile.snapchat" />
                            </h3>
                            <p hidden={!user?.profile?.snapchat}>
                                <a
                                    href={'https://www.snapchat.com/profile/' + user?.profile?.snapchat}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {user?.profile?.snapchat}
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    render() {
        const { loading, user } = this.state;
        const playerProps = playerService.getProps(this.props, user);
        const fullName =
            user &&
            `${user?.profile?.givenName} ${user?.profile?.familyName} - ${this.props.intl.formatMessage({
                id: 'profile',
            })}`;

        return (
            <>
                <HeadMetadata title={fullName} />
                <LayoutComponent
                    page="player-profile"
                    content={this.renderContent}
                    header={this.renderHeader}
                    loading={loading === 'initializing'}
                    {...playerProps}
                />
            </>
        );
    }
}
export default injectIntl(PlayerProfilePage);

type ParamsType = { id: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    user?: User;
    sports?: Sport[];
    sport?: Sport;
    userSport?: UserSport;
    userSports: UserSport[];
    positions: Position[];
    team?: string;
    loading?: 'initializing' | 'loading';
    searchText?: string;
    userRating?: UserRating;
    seasons: Season[];
    seasonId?: number;
    userOrganizationsBySport: UserOrganizationMember[];
    organization?: string;
}
